import { CreditCard, Receipt, Zap, Check, ArrowUpRight, Loader2, ExternalLink } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { trpc } from "@/lib/trpc";
import seedData from "../../../seed-data.json";
import type { PlanTier } from "@/types/billing";

const pricingTiers = seedData.pricing.tiers;

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * BillingPage - Billing and subscription management
 *
 * Features:
 * - Current plan card (from Stripe via tRPC)
 * - Pricing tiers from seed-data.json
 * - Upgrade via Stripe Checkout
 * - Manage via Stripe Customer Portal
 */
export function BillingPage() {
  const { t } = useTranslation("dashboard");
  const { t: tLanding } = useTranslation("landing");

  const getPlanDisplayName = (plan: PlanTier): string => {
    if (plan === "free") return "Free";
    return String(tLanding(`pricing.${plan}.name`, { defaultValue: plan }));
  };

  const subscription = trpc.billing.getSubscription.useQuery();
  const createCheckout = trpc.billing.createCheckoutSession.useMutation({
    onSuccess: (data) => {
      window.location.href = data.url;
    },
  });
  const createPortal = trpc.billing.createPortalSession.useMutation({
    onSuccess: (data) => {
      window.location.href = data.url;
    },
  });

  const currentPlan = subscription.data?.plan ?? "free";
  const isFreePlan = currentPlan === "free";

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="space-y-2">
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">
          {t("pages.billing.title")}
        </h1>
        <p className="text-base text-muted-foreground md:text-lg">
          {t("pages.billing.description")}
        </p>
      </div>

      <Separator />

      {/* Current plan summary */}
      {subscription.isLoading ? (
        <Card>
          <CardContent className="flex items-center justify-center p-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-primary/20 bg-primary/10">
                  <Zap className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-foreground">
                      {getPlanDisplayName(currentPlan)} Plan
                    </h3>
                    <span className="rounded-full border border-primary/20 bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                      {subscription.data?.status === "active"
                        ? "Active"
                        : subscription.data?.status === "trialing"
                          ? "Trial"
                          : (subscription.data?.status ?? "Active")}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {isFreePlan
                      ? "No active subscription"
                      : subscription.data?.cancelAtPeriodEnd
                        ? `Cancels on ${formatDate(subscription.data.currentPeriodEnd)}`
                        : `Renews on ${formatDate(subscription.data?.currentPeriodEnd ?? null)}`}
                  </p>
                </div>
              </div>
              {!isFreePlan && (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => createPortal.mutate()}
                    disabled={createPortal.isPending}
                  >
                    {createPortal.isPending ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Receipt className="mr-2 h-4 w-4" />
                    )}
                    View Invoices
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => createPortal.mutate()}
                    disabled={createPortal.isPending}
                  >
                    {createPortal.isPending ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <CreditCard className="mr-2 h-4 w-4" />
                    )}
                    Manage Subscription
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Plans */}
      <section className="space-y-4">
        <h2 className="text-xs font-medium tracking-widest text-muted-foreground uppercase">
          Available Plans
        </h2>

        <div className="grid gap-4 md:grid-cols-3">
          {pricingTiers.map((tier) => {
            const tierId = tier.id as PlanTier;
            const isCurrent = tierId === currentPlan;
            const isEnterprise = tier.price.amount === null;
            const isHighlighted = tier.highlighted;
            const tierName = String(tLanding(`pricing.${tier.id}.name`, { defaultValue: tier.id }));
            const tierFeatures = tLanding(`pricing.${tier.id}.features`, {
              returnObjects: true,
              defaultValue: [] as string[],
            }) as string[];

            return (
              <Card
                key={tier.id}
                className={`relative ${isHighlighted ? "border-primary/30" : ""}`}
              >
                <CardContent className="p-6">
                  {isCurrent && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary px-3 py-0.5 text-xs font-medium text-primary-foreground">
                      Current Plan
                    </div>
                  )}

                  <div className="text-center">
                    <h3 className="text-lg font-semibold text-foreground">{tierName}</h3>
                    <div className="mt-2">
                      <span className="text-3xl font-bold text-foreground">
                        {tier.price.amount !== null ? `$${tier.price.amount}` : "Custom"}
                      </span>
                      <span className="text-sm text-muted-foreground">
                        {" "}
                        / {tier.price.amount !== null ? tier.price.period : "contact us"}
                      </span>
                    </div>
                  </div>

                  <ul className="mt-6 space-y-3">
                    {tierFeatures.map((feature: string) => (
                      <li
                        key={feature}
                        className="flex items-center gap-2 text-sm text-muted-foreground"
                      >
                        <Check className="h-4 w-4 text-primary" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  {isEnterprise ? (
                    <Button className="mt-6 w-full" variant="outline" asChild>
                      <a href="mailto:sales@example.com">
                        Contact Sales
                        <ExternalLink className="ml-2 h-4 w-4" />
                      </a>
                    </Button>
                  ) : isCurrent ? (
                    <Button className="mt-6 w-full" variant="outline" disabled>
                      Current Plan
                    </Button>
                  ) : (
                    <Button
                      className="mt-6 w-full"
                      onClick={() =>
                        createCheckout.mutate({
                          plan: tierId as "starter" | "professional",
                        })
                      }
                      disabled={createCheckout.isPending}
                    >
                      {createCheckout.isPending ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : null}
                      Upgrade
                      <ArrowUpRight className="ml-2 h-4 w-4" />
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>
    </div>
  );
}

export default BillingPage;
